import {
  consumerQuoteTotalPremiums,
  isTraditionalConsumerQuote,
  paymentScheduleOfQuote,
  type ConsumerQuote,
  type ConsumerQuoteProjection,
} from '@shared/consumerPlan';
import { money } from '../lib/format';
import { DetailList, type DetailRow } from './figures';

const DAYS_PER_MONTH = 30;

function benefitRows(quote: ConsumerQuote): DetailRow[] {
  if (isTraditionalConsumerQuote(quote)) {
    return [
      { label: 'Monthly maximum today', value: money(quote.dailyBenefitAmount * DAYS_PER_MONTH) },
      { label: 'Total maximum today', value: money(quote.benefitPool) },
      { label: 'Benefit growth', value: quote.bioPercent },
      { label: 'Waiting period', value: `${quote.eliminationPeriod} days` },
    ];
  }

  return [
    { label: 'Monthly maximum today', value: money(quote.ltcBenefitMonthlyIndividual) },
    { label: 'Total maximum today', value: money(quote.ltcBenefitTotal) },
    { label: 'Benefit growth', value: quote.inflationBenefitOption ?? 'None' },
    { label: 'Waiting period', value: quote.eliminationPeriod ?? 'See policy' },
  ];
}

export function PolicyTermsList({
  quote,
  projection,
  careStartAge,
  yearsUntilCare,
}: {
  quote: ConsumerQuote;
  projection: ConsumerQuoteProjection | undefined;
  careStartAge: number;
  yearsUntilCare: number;
}) {
  const isOngoing = paymentScheduleOfQuote(quote) === 'ongoing';
  const terms = projection?.projection?.terms;

  return (
    <DetailList
      rows={[
        ...benefitRows(quote),
        ...(projection
          ? [
              { label: `Most it could pay by age ${careStartAge}`, value: money(projection.maxBenefitAtCareStart) },
              { label: 'Projected payments for your scenario', value: money(projection.projectedPayout) },
            ]
          : []),
        ...(terms
          ? [{ label: 'Benefit type', value: terms.isIndemnity ? 'Cash benefit' : 'Expense reimbursement' }]
          : []),
        {
          label: isOngoing ? `Premiums until age ${careStartAge}*` : 'Total premiums',
          value: money(consumerQuoteTotalPremiums(quote, yearsUntilCare)),
        },
      ]}
    />
  );
}
